import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import ContactsManager from './components/ContactsManager';
import CreateAlert from './components/CreateAlert';
import UserManager from './components/UserManager';
import PaginaConsentimiento from './PaginaConsentimiento';
import Informes from './components/Informes';
import PlantillasManager from './components/PlantillasManager';
import AsistenteIA from './components/AsistenteIA';
import ApprovalQueue from './components/ApprovalQueue';
import ListasManager from './components/ListasManager';
import ResetPassword from './components/ResetPassword';
import ClientesManager from './components/ClientesManager';
import PaginaCanalOficial from './components/PaginaCanalOficial';
import { LogOut, Menu, X, Shield, Users, Bell, FileText, User, ClipboardList, Bot, Moon, Sun, CheckSquare, List, Building2 } from 'lucide-react';
import { getAuthorizedHeaders, clearSessionStorage, isSessionValidLocally, touchSessionActivity } from './utils/authorizedHeaders';



// Ruta Protegida Componente (verifica token local + validación contra backend)
const ProtectedRoute = ({ children, usuario, verificandoSesion }) => {
    if (verificandoSesion) {
        return (
            <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center text-white p-6">
                <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mb-4" />
                <p className="text-sm font-medium text-gray-300">Verificando credenciales de seguridad...</p>
            </div>
        );
    }
    if (!usuario || !isSessionValidLocally()) {
        clearSessionStorage();
        return <Navigate to="/" replace />;
    }
    return children;
};

// Componente Layout Principal
const MainLayout = ({ children, usuario, setUsuario, darkMode, toggleDarkMode, clienteActivo, setClienteActivo, clientes = [] }) => {
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    // Cerrar menú móvil automáticamente al cambiar de ruta
    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location.pathname]);

    const handleLogout = () => {
        clearSessionStorage();
        setUsuario(null);
        navigate('/');
    };

    const menuItems = [
        { id: 'dashboard', label: 'Panel de Control', icon: Shield, path: '/dashboard' },
        { id: 'contactos', label: 'Directorio Global', icon: Users, path: '/contactos' },
        { id: 'listas', label: 'Mis Listas', icon: List, path: '/listas' },
        { id: 'plantillas', label: 'Plantillas', icon: ClipboardList, path: '/plantillas' },
        { id: 'alertas', label: 'Nueva Alerta', icon: Bell, path: '/crear-alerta' },
        { id: 'informes', label: 'Informes', icon: FileText, path: '/informes' },
    ];

    if (usuario?.rol !== 'operador') {
        menuItems.push({ id: 'aprobaciones', label: 'Aprobaciones', icon: CheckSquare, path: '/aprobaciones' });
    }

    if (usuario?.rol === 'superadmin') {
        menuItems.push({ id: 'clientes', label: 'Clientes / Emisores', icon: Building2, path: '/clientes' });
    }

    if (usuario?.rol === 'admin' || usuario?.rol === 'superadmin') {
        menuItems.push({ id: 'usuarios', label: 'Usuarios', icon: User, path: '/usuarios' });
    } else {
        menuItems.push({ id: 'usuarios', label: 'Mi Perfil', icon: User, path: '/usuarios' });
    }

    // Pestaña de IA siempre al final
    menuItems.push({ id: 'asistente-ia', label: 'Asistente IA', icon: Bot, path: '/asistente-ia' });

    const cambiarCliente = (idVal) => {
        const found = clientes.find(c => c.id === parseInt(idVal));
        if (found) {
            setClienteActivo(found);
            localStorage.setItem('clienteActivo', JSON.stringify(found));
        }
    };

    return (
        <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex transition-colors duration-300 relative">
            {/* Backdrop oscuro para móvil cuando el menú lateral está abierto */}
            {mobileMenuOpen && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-xs z-40 md:hidden transition-opacity"
                    onClick={() => setMobileMenuOpen(false)}
                />
            )}

            {/* Sidebar: Drawer deslizable en móvil (< md) y barra fija colapsable en escritorio (md+) */}
            <aside
                className={`bg-primary-900 dark:bg-gray-950 text-white flex flex-col fixed inset-y-0 left-0 z-50 transform transition-all duration-300 ease-in-out
                    w-72 ${mobileMenuOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'}
                    md:relative md:translate-x-0 md:shadow-none ${sidebarOpen ? 'md:w-72' : 'md:w-20'}`}
            >
                <div className="p-4 flex items-center justify-between border-b border-primary-800 dark:border-gray-800">
                    <span className={`font-bold text-sm leading-tight text-primary-100 uppercase tracking-wider ${!sidebarOpen ? 'md:hidden' : ''}`}>
                        Sistema de Avisos y Alertas
                    </span>
                    {/* Botón cerrar en móvil */}
                    <button
                        onClick={() => setMobileMenuOpen(false)}
                        className="p-2 hover:bg-primary-800 dark:hover:bg-gray-800 rounded md:hidden"
                        aria-label="Cerrar menú"
                    >
                        <X size={20} />
                    </button>
                    {/* Botón colapsar en escritorio */}
                    <button
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        className="hidden md:inline-flex p-2 hover:bg-primary-800 dark:hover:bg-gray-800 rounded"
                        aria-label="Alternar barra lateral"
                    >
                        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>

                {/* Selector de cliente dentro del cajón móvil para cambiar fácilmente desde el teléfono */}
                {clientes && clientes.length > 0 && (
                    <div className="px-4 pt-3 pb-1 md:hidden">
                        <div className="bg-primary-800/70 dark:bg-gray-900 px-3 py-2 rounded-xl border border-primary-700 dark:border-gray-800">
                            <label className="block text-[10px] uppercase tracking-wider text-primary-300 font-semibold mb-1">
                                Entidad / Cliente activo
                            </label>
                            <select
                                value={clienteActivo?.id || ''}
                                onChange={(e) => cambiarCliente(e.target.value)}
                                className="w-full text-xs font-bold bg-transparent text-white border-none outline-none cursor-pointer"
                            >
                                {clientes.map(c => (
                                    <option key={c.id} value={c.id} className="text-gray-900">
                                        {c.nombre}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}

                <nav className="flex-1 py-3 overflow-y-auto">
                    <ul className="space-y-1">
                        {menuItems.map((item) => (
                            <li key={item.id}>
                                <button
                                    onClick={() => {
                                        navigate(item.path);
                                        setMobileMenuOpen(false);
                                    }}
                                    className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-primary-800 dark:hover:bg-gray-800 transition text-left ${
                                        location.pathname === item.path
                                            ? 'bg-primary-800 dark:bg-gray-800 border-r-4 border-yellow-400 font-semibold'
                                            : ''
                                    }`}
                                >
                                    <item.icon size={20} className="shrink-0" />
                                    <span className={`truncate ${!sidebarOpen ? 'md:hidden' : ''}`}>{item.label}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className="p-4 border-t border-primary-800 dark:border-gray-800 space-y-2">
                    {/* Dark Mode Toggle */}
                    <button
                        onClick={toggleDarkMode}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-primary-800 dark:hover:bg-gray-800 rounded transition text-primary-200 hover:text-white"
                        title={darkMode ? 'Modo claro' : 'Modo oscuro'}
                    >
                        {darkMode ? <Sun size={20} className="shrink-0" /> : <Moon size={20} className="shrink-0" />}
                        <span className={!sidebarOpen ? 'md:hidden' : ''}>{darkMode ? 'Modo Claro' : 'Modo Oscuro'}</span>
                    </button>
                    <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-600 rounded transition text-red-200 hover:text-white"
                    >
                        <LogOut size={20} className="shrink-0" />
                        <span className={!sidebarOpen ? 'md:hidden' : ''}>Cerrar Sesión</span>
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                <header className="bg-white dark:bg-gray-800 shadow-sm py-2.5 px-3 sm:px-6 flex justify-between items-center gap-2 transition-colors duration-300">
                    <div className="flex items-center gap-2.5 sm:gap-4 min-w-0">
                        {/* Botón hamburguesa solo en móvil */}
                        <button
                            onClick={() => setMobileMenuOpen(true)}
                            className="md:hidden p-2 -ml-1 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 shrink-0"
                            aria-label="Abrir menú"
                        >
                            <Menu size={22} />
                        </button>

                        <h2 className="text-base sm:text-xl font-semibold text-gray-800 dark:text-gray-100 truncate">
                            {menuItems.find(i => i.path === location.pathname)?.label || 'Panel de Control'}
                        </h2>

                        {/* SELECTOR GLOBAL DE CLIENTE ACTIVO (TENANT SWITCHER) */}
                        {clientes && clientes.length > 0 && (
                            <div className="hidden sm:flex items-center gap-2 bg-gray-50 dark:bg-gray-700/60 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600">
                                <Building2 size={16} className="text-primary-600 dark:text-primary-400 shrink-0" />
                                <span className="text-xs text-gray-500 dark:text-gray-400 font-medium hidden lg:inline">Cliente:</span>
                                <select
                                    value={clienteActivo?.id || ''}
                                    onChange={(e) => cambiarCliente(e.target.value)}
                                    className="text-xs font-bold bg-transparent text-gray-900 dark:text-gray-100 border-none outline-none cursor-pointer pr-1 max-w-[160px] lg:max-w-none truncate"
                                >
                                    {clientes.map(c => (
                                        <option key={c.id} value={c.id} className="text-gray-900 dark:text-gray-100">
                                            {c.nombre} {c.sms_sender_id ? `[SMS: ${c.sms_sender_id}]` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-2.5 sm:gap-4 shrink-0">
                        <div className="text-right hidden xs:block sm:block">
                            <p className="text-xs sm:text-sm font-semibold text-gray-800 dark:text-gray-100 leading-tight max-w-[110px] sm:max-w-none truncate">
                                {usuario?.nombre}
                            </p>
                            <p className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400 capitalize">{usuario?.rol}</p>
                        </div>
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center text-primary-700 dark:text-primary-300 font-bold text-sm sm:text-base shrink-0">
                            {usuario?.nombre?.charAt(0)}
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900 p-3 sm:p-6 transition-colors duration-300">
                    {children}
                </main>
            </div>
        </div>
    );
};


function App() {
    const [usuario, setUsuario] = useState(() => {
        if (!isSessionValidLocally()) return null;
        try {
            return JSON.parse(localStorage.getItem('user')) || null;
        } catch (e) {
            clearSessionStorage();
            return null;
        }
    });
    const [verificandoSesion, setVerificandoSesion] = useState(() => isSessionValidLocally());
    const [contactos, setContactos] = useState([]);
    const [estadisticas] = useState({ enviados: 0, pendientes: 0 });
    const [plantillas, setPlantillas] = useState([]);
    const [loginData, setLoginData] = useState({ email: '', password: '' });
    const [errorLogin, setErrorLogin] = useState('');
    const [mensajeReset, setMensajeReset] = useState('');
    const [mfaState, setMfaState] = useState({ required: false, token: null, email: null });

    // Dark mode
    const [darkMode, setDarkMode] = useState(() => {
        return localStorage.getItem('darkMode') === 'true';
    });

    useEffect(() => {
        if (darkMode) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
        localStorage.setItem('darkMode', darkMode);
    }, [darkMode]);

    const toggleDarkMode = () => setDarkMode(prev => !prev);

    // Estados para ContactsManager
    const [nuevoContacto, setNuevoContacto] = useState({
        nombre: '', email: '', telefono: '', whatsapp: '',
        tipo_usuario: 'Residente', tipo_punto_critico: '',
        zona: '', distrito: '', ciudad: 'Málaga', pais: 'España',
        rol: 'poblacion', distrito_municipal: '',
        tipo_vulnerabilidad: 'ninguna', medio_comunicacion_preferido: 'whatsapp',
        suscrito_email: false, suscrito_sms: false, suscrito_whatsapp: false
    });
    const [contactoEditando, setContactoEditando] = useState(null);

    // Estados para UserManager (ahora reales, desde la API)
    const [usuarios, setUsuarios] = useState([]);
    const [nuevoUsuario, setNuevoUsuario] = useState({ nombre: '', email: '', password: '', rol: 'operador' });
    const [usuarioEditando, setUsuarioEditando] = useState(null);

    // Estados para Alertas
    const [alertasPendientes, setAlertasPendientes] = useState([]);
    const [alertasProgramadas, setAlertasProgramadas] = useState([]);
    const [tiposAlerta, setTiposAlerta] = useState([]);

    // Estado para listas de contactos (grupos de importación)
    const [listas, setListas] = useState([]);

    // Multi-tenant Clientes
    const [clientes, setClientes] = useState([]);
    const [clienteActivo, setClienteActivo] = useState(() => {
        try {
            const c = localStorage.getItem('clienteActivo');
            return c ? JSON.parse(c) : null;
        } catch (e) { return null; }
    });

    const fetchClientes = async () => {
        try {
            const res = await fetch(`${API_BASE}/api/clientes`, { headers: getAuthorizedHeaders() });
            if (res.ok) {
                const d = await res.json();
                if (d.success) {
                    setClientes(d.data || []);
                    if ((!clienteActivo || !d.data.some(c => c.id === clienteActivo.id)) && d.data.length > 0) {
                        const rcv = d.data.find(c => c.codigo === 'RCV_FINANCE') || d.data[0];
                        setClienteActivo(rcv);
                        localStorage.setItem('clienteActivo', JSON.stringify(rcv));
                    }
                }
            }
        } catch (e) { console.error('Error cargando clientes:', e); }
    };

    //const API_BASE = 'http://localhost:3001';
    const API_BASE = 'https://warngen-api-352514293915.europe-west1.run.app';

    // Verificación criptográfica contra el servidor al abrir la app o entrar por un enlace directo
    useEffect(() => {
        let cancelado = false;
        const verificarSesionRemota = async () => {
            if (!isSessionValidLocally()) {
                clearSessionStorage();
                if (!cancelado) {
                    setUsuario(null);
                    setVerificandoSesion(false);
                }
                return;
            }
            try {
                const res = await fetch(`${API_BASE}/api/auth/me`, {
                    method: 'GET',
                    headers: getAuthorizedHeaders()
                });
                if (!res.ok) {
                    clearSessionStorage();
                    if (!cancelado) {
                        setUsuario(null);
                        setVerificandoSesion(false);
                    }
                    return;
                }
                const data = await res.json();
                if (data.success && data.user) {
                    localStorage.setItem('user', JSON.stringify(data.user));
                    touchSessionActivity();
                    if (!cancelado) {
                        setUsuario(data.user);
                        setVerificandoSesion(false);
                    }
                } else {
                    clearSessionStorage();
                    if (!cancelado) {
                        setUsuario(null);
                        setVerificandoSesion(false);
                    }
                }
            } catch (e) {
                console.error('Error verificando sesión:', e);
                if (!cancelado) {
                    setVerificandoSesion(false);
                }
            }
        };
        verificarSesionRemota();
        return () => { cancelado = true; };
    }, []);

    useEffect(() => {
        if (usuario && !verificandoSesion) {
            fetchContactos();
            fetchPlantillas();
            fetchTiposAlerta();
            fetchUsuarios();
            fetchListas();
            fetchClientes();
        }
    }, [usuario?.id, verificandoSesion]);


    // Logout automático por inactividad (30 minutos, persistido también entre cierres de pestaña)
    useEffect(() => {
        if (!usuario) return;

        let inactivityTimer;
        let lastWrite = 0;
        
        const handleAutoLogout = () => {
            clearSessionStorage();
            setUsuario(null);
            alert('Sesión finalizada por inactividad por su seguridad.');
            window.location.href = '/';
        };

        const resetTimer = () => {
            // Si al volver a la pestaña ya pasaron más de 30 min desde la última actividad, cerrar sesión inmediatamente
            const lastAct = Number(localStorage.getItem('lastActivityAt') || 0);
            if (lastAct > 0 && Date.now() - lastAct > 1800000) {
                handleAutoLogout();
                return;
            }
            const now = Date.now();
            if (now - lastWrite > 15000) {
                touchSessionActivity();
                lastWrite = now;
            }
            clearTimeout(inactivityTimer);
            // 30 minutos = 30 * 60 * 1000 = 1800000 ms
            inactivityTimer = setTimeout(handleAutoLogout, 1800000); 
        };

        const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart', 'focus'];
        
        events.forEach(event => {
            window.addEventListener(event, resetTimer);
        });

        // Iniciar el temporizador al principio
        resetTimer();

        return () => {
            clearTimeout(inactivityTimer);
            events.forEach(event => {
                window.removeEventListener(event, resetTimer);
            });
        };
    }, [usuario]);

    // Función global para registrar actividad en la BD
    const registrarActividad = async (mensaje, modulo, tipo = 'info', datosAdicionales = {}, userIdOverride = null) => {
        const uid = userIdOverride || usuario?.id;
        if (!uid) return;
        try {
            await fetch(`${API_BASE}/api/logs`, {
                method: 'POST',
                headers: getAuthorizedHeaders(),
                body: JSON.stringify({ tipo, modulo, mensaje, datos_adicionales: datosAdicionales, usuario_id: uid })
            });
        } catch (error) {
            console.error('Error registrando actividad:', error);
        }
    };

    const fetchPlantillas = async () => {
        try {
            const res = await fetch(`${API_BASE}/api/plantillas`, {
                method: 'GET',
                headers: getAuthorizedHeaders(),
            });     
            if (res.ok) setPlantillas(await res.json());
        } catch (error) { console.error('Error fetching plantillas:', error); }
    };

    const fetchContactos = async () => {
        try {
            const res = await fetch(`${API_BASE}/api/contactos`, {
                method: 'GET',
                headers: getAuthorizedHeaders(),
            });
            if (res.ok) setContactos(await res.json());
        } catch (error) { console.error('Error fetching contactos:', error); }
    };

    const fetchListas = async () => {
        try {
            const res = await fetch(`${API_BASE}/api/listas`, {
                headers: getAuthorizedHeaders()
            });
            if (res.ok) {
                const data = await res.json();
                if (data.success) setListas(data.data || []);
            }
        } catch (error) { console.error('Error fetching listas:', error); }
    };

    const fetchContactosDeLista = async (listaId) => {
        try {
            const res = await fetch(`${API_BASE}/api/listas/${listaId}/contactos`, {
                headers: getAuthorizedHeaders()
            });
            if (res.ok) {
                const data = await res.json();
                return { success: data.success, contactos: data.data || [] };
            }
            return { success: false, contactos: [] };
        } catch (error) {
            console.error('Error fetching contactos de lista:', error);
            return { success: false, contactos: [] };
        }
    };

    const fetchTiposAlerta = async () => {
        try {
            const res = await fetch(`${API_BASE}/api/tipos-alerta`, {
                method: 'GET',
                headers: getAuthorizedHeaders(),
            });
            if (res.ok) setTiposAlerta(await res.json());
        } catch (error) { console.error('Error fetching tipos alerta:', error); }
    };

    // --- Gestión de Usuarios (real API) ---
    const fetchUsuarios = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE}/api/usuarios`, {
                //headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                headers: getAuthorizedHeaders(),
            });
            if (res.ok) setUsuarios(await res.json());
        } catch (error) { console.error('Error fetching usuarios:', error); }
    };

    const guardarUsuario = async () => {
        if (!nuevoUsuario.nombre || !nuevoUsuario.email) {
            alert('El nombre y el email son obligatorios');
            return;
        }
        if (!usuarioEditando && !nuevoUsuario.password) {
            alert('La contraseña es obligatoria al crear un usuario');
            return;
        }
        try {
            const url = usuarioEditando
                ? `${API_BASE}/api/usuarios/${usuarioEditando.id}`
                : `${API_BASE}/api/usuarios`;
            const method = usuarioEditando ? 'PUT' : 'POST';
            const res = await fetch(url, {
                method,
                headers: getAuthorizedHeaders(),
                body: JSON.stringify(nuevoUsuario)
            });
            const data = await res.json();
            if (data.success) {
                fetchUsuarios();
                setNuevoUsuario({ nombre: '', email: '', password: '', rol: 'operador' });
                setUsuarioEditando(null);
                registrarActividad(
                    usuarioEditando ? `Usuario ${nuevoUsuario.nombre} editado` : `Usuario ${nuevoUsuario.nombre} creado`,
                    'usuarios', 'info'
                );
                alert(usuarioEditando ? 'Usuario actualizado correctamente' : 'Usuario creado correctamente');
            } else {
                alert('Error: ' + (data.error || 'Error al guardar usuario'));
            }
        } catch (error) {
            console.error(error);
            alert('Error de conexión al guardar usuario');
        }
    };

    const eliminarUsuario = async (id) => {
        if (!window.confirm('¿Seguro que deseas eliminar este usuario? Esta acción no se puede deshacer.')) return;
        try {
            const res = await fetch(`${API_BASE}/api/usuarios/${id}`, { method: 'DELETE', headers: getAuthorizedHeaders(), });
            const data = await res.json();
            if (data.success) {
                fetchUsuarios();
                registrarActividad(`Usuario ID ${id} eliminado`, 'usuarios', 'warning');
            } else {
                alert('Error: ' + (data.error || 'No se pudo eliminar el usuario'));
            }
        } catch (error) {
            console.error(error);
            alert('Error de conexión al eliminar usuario');
        }
    };

    const toggleUsuario = async (id) => {
        try {
            const res = await fetch(`${API_BASE}/api/usuarios/${id}/toggle`, { method: 'PATCH', headers: getAuthorizedHeaders(), });
            const data = await res.json();
            if (data.success) {
                fetchUsuarios();
                registrarActividad(`Usuario ID ${id} ${data.activo ? 'activado' : 'desactivado'}`, 'usuarios', 'info');
            } else {
                alert('Error: ' + (data.error || 'No se pudo cambiar el estado del usuario'));
            }
        } catch (error) {
            console.error(error);
            alert('Error de conexión al cambiar estado del usuario');
        }
    };

    // --- Handlers Autenticación y MFA ---
    const handleLogin = async (e) => {
        e.preventDefault();
        setErrorLogin('');
        try {
            const res = await fetch(`${API_BASE}/api/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(loginData)
            });
            const data = await res.json();
            if (data.success) {
                if (data.mfa_required) {
                    setMfaState({ required: true, token: data.mfa_token, email: data.email });
                    setErrorLogin('');
                } else {
                    const userObj = data.user;
                    localStorage.setItem('user', JSON.stringify(userObj));
                    if (data.token) localStorage.setItem('token', data.token);
                    touchSessionActivity();
                    setVerificandoSesion(false);
                    setUsuario(userObj);
                    registrarActividad('Inicio de sesión exitoso', 'auth', 'info', {}, userObj.id);
                }
            } else {
                setErrorLogin(data.error);
            }
        } catch (error) {
            setErrorLogin('Error de conexión con el servidor');
        }
    };

    const handleMfaVerify = async (code) => {
        setErrorLogin('');
        try {
            const res = await fetch(`${API_BASE}/api/auth/mfa/verify-login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ mfa_token: mfaState.token, code })
            });
            const data = await res.json();
            if (data.success) {
                const userObj = data.user;
                localStorage.setItem('user', JSON.stringify(userObj));
                if (data.token) localStorage.setItem('token', data.token);
                touchSessionActivity();
                setVerificandoSesion(false);
                setUsuario(userObj);
                setMfaState({ required: false, token: null, email: null });
                registrarActividad('Inicio de sesión con 2FA exitoso (ENS)', 'auth', 'info', {}, userObj.id);
            } else {
                setErrorLogin(data.error || 'Código 2FA incorrecto o caducado');
            }
        } catch (error) {
            setErrorLogin('Error de conexión con el servidor');
        }
    };

    const cancelMfa = () => {
        setMfaState({ required: false, token: null, email: null });
        setErrorLogin('');
    };

    const handlePasswordReset = async (email) => {
        setErrorLogin('');
        setMensajeReset('');
        if (!email) {
            setErrorLogin('Introduce tu email para restablecer la contraseña');
            return;
        }
        try {
            const res = await fetch(`${API_BASE}/api/auth/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });
            const data = await res.json();
            if (data.success) {
                setMensajeReset(data.message);
            } else {
                setErrorLogin(data.error || 'Error al procesar solicitud');
            }
        } catch (error) {
            setErrorLogin('Error de conexión con el servidor');
        }
    };


    const guardarContacto = async () => {
        try {
            const url = contactoEditando
                ? `${API_BASE}/api/contactos/${contactoEditando.id}`
                : `${API_BASE}/api/contactos`;
            const method = contactoEditando ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method,
                headers: getAuthorizedHeaders(),
                body: JSON.stringify(nuevoContacto)
            });
            if (res.ok) {
                fetchContactos();
                setNuevoContacto({
                    nombre: '', email: '', telefono: '', whatsapp: '',
                    tipo_usuario: 'Residente', tipo_punto_critico: '',
                    zona: '', distrito: '', ciudad: 'Málaga', pais: 'España',
                    rol: 'poblacion', distrito_municipal: '',
                    tipo_vulnerabilidad: 'ninguna', medio_comunicacion_preferido: 'whatsapp',
                    suscrito_email: false, suscrito_sms: false, suscrito_whatsapp: false
                });
                setContactoEditando(null);
                registrarActividad(`Contacto ${nuevoContacto.nombre} creado/actualizado`, 'contactos', 'info');
                alert('Contacto guardado correctamente');
            } else {
                const data = await res.json();
                alert('Error al guardar contacto: ' + (data.error || 'Error desconocido'));
            }
        } catch (error) {
            console.error(error);
            alert('Error de conexión al guardar contacto');
        }
    };

    // Sube el CSV a Google Cloud Storage y encola el trabajo en Pub/Sub
    const importarContactosCSV = async (archivoFile, nombreLista) => {
        try {
            const token = localStorage.getItem('token');
            const formData = new FormData();
            formData.append('file', archivoFile);
            formData.append('nombre', nombreLista || archivoFile.name.replace('.csv', ''));
            formData.append('targetRole', 'Residente');
            formData.append('notificationPreference', 'NONE');

            const res = await fetch(`${API_BASE}/api/bulk/upload`, {
                method: 'POST',
                headers: token ? { 'Authorization': `Bearer ${token}` } : {},
                body: formData
            });
            const data = await res.json();
            if (data.success) {
                registrarActividad(
                    `Importación masiva iniciada — Lista: "${data.nombreLista}" (Job #${data.jobId})`,
                    'contactos', 'info', { jobId: data.jobId, nombreLista: data.nombreLista }
                );
                fetchListas(); // Refrescar listas inmediatamente
            }
            return data;
        } catch (error) {
            console.error('Error iniciando importación masiva:', error);
            return { success: false, error: 'Error de conexión al subir el archivo' };
        }
    };

    // Consulta el estado de un job de importación (para polling desde el componente)
    const consultarJobImportacion = async (jobId) => {
        try {
            const res = await fetch(`${API_BASE}/api/bulk/job/${jobId}`, {
                headers: getAuthorizedHeaders()
            });
            const data = await res.json();
            if (data.success && ['COMPLETED', 'PARTIAL_SUCCESS'].includes(data.job?.status)) {
                fetchContactos();
                fetchListas(); // Refrescar listas al completar
            }
            return data;
        } catch (error) {
            console.error('Error consultando job:', error);
            return { success: false };
        }
    };

    const descargarErroresImportacion = async (jobId) => {
        try {
            const res = await fetch(`${API_BASE}/api/bulk/job/${jobId}/error-download`, {
                headers: getAuthorizedHeaders()
            });
            const data = await res.json();
            if (data.success && data.url) {
                window.open(data.url, '_blank');
            }
            return data;
        } catch (error) {
            console.error('Error descargando errores:', error);
            return { success: false };
        }
    };

    const eliminarContacto = async (id) => {
        if (!window.confirm('¿Seguro?')) return;
        try {
            const res = await fetch(`${API_BASE}/api/contactos/${id}`, { method: 'DELETE', headers: getAuthorizedHeaders(), });
            if (res.ok) {
                fetchContactos();
                registrarActividad(`Contacto ID ${id} eliminado`, 'contactos', 'warning');
            }
        } catch (error) { console.error(error); }
    };

    const darkProps = { darkMode, toggleDarkMode, clienteActivo, setClienteActivo, clientes };

    return (
        <Router>
            <Routes>
                {/* Ruta Pública: Consentimiento */}
                <Route path="/consentimiento/:token" element={<PaginaConsentimiento />} />

                {/* Ruta Pública: Página Oficial de Canal para Verificación de Meta WhatsApp */}
                <Route path="/canal/:slug" element={<PaginaCanalOficial />} />

                {/* Ruta Pública: Restablecimiento Seguro de Contraseña (ENS mp.acc.5) */}
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* Ruta Login */}
                <Route path="/" element={
                    !usuario ? (
                        <Login
                            loginData={loginData}
                            setLoginData={setLoginData}
                            handleLogin={handleLogin}
                            errorLogin={errorLogin}
                            darkMode={darkMode}
                            toggleDarkMode={toggleDarkMode}
                            handlePasswordReset={handlePasswordReset}
                            mensajeReset={mensajeReset}
                            mfaState={mfaState}
                            handleMfaVerify={handleMfaVerify}
                            cancelMfa={cancelMfa}
                        />
                    ) : (
                        <Navigate to="/dashboard" />
                    )
                } />


                {/* Rutas Protegidas */}
                <Route path="/dashboard" element={
                    <ProtectedRoute usuario={usuario} verificandoSesion={verificandoSesion}>
                        <MainLayout usuario={usuario} setUsuario={setUsuario} {...darkProps}>
                            <Dashboard
                                estadisticas={estadisticas}
                                contactos={contactos}
                                plantillas={plantillas}
                                listas={listas}
                                clienteActivo={clienteActivo}
                                usuario={usuario}
                                setVista={(vista) => window.location.href = `/${vista}`}
                                iniciarFlujoAlerta={() => window.location.href = '/crear-alerta'}
                                darkMode={darkMode}
                            />
                        </MainLayout>
                    </ProtectedRoute>
                } />

                <Route path="/informes" element={
                    <ProtectedRoute usuario={usuario} verificandoSesion={verificandoSesion}>
                        <MainLayout usuario={usuario} setUsuario={setUsuario} {...darkProps}>
                            <Informes usuario={usuario} darkMode={darkMode} />
                        </MainLayout>
                    </ProtectedRoute>
                } />

                <Route path="/asistente-ia" element={
                    <ProtectedRoute usuario={usuario} verificandoSesion={verificandoSesion}>
                        <MainLayout usuario={usuario} setUsuario={setUsuario} {...darkProps}>
                            <AsistenteIA darkMode={darkMode} />
                        </MainLayout>
                    </ProtectedRoute>
                } />

                <Route path="/contactos" element={
                    <ProtectedRoute usuario={usuario} verificandoSesion={verificandoSesion}>
                        <MainLayout usuario={usuario} setUsuario={setUsuario} {...darkProps}>
                            <ContactsManager
                                contactos={contactos}
                                nuevoContacto={nuevoContacto}
                                setNuevoContacto={setNuevoContacto}
                                contactoEditando={contactoEditando}
                                guardarContacto={guardarContacto}
                                iniciarEdicionContacto={(c) => {
                                    setContactoEditando(c);
                                    setNuevoContacto({
                                        nombre: c.nombre || '', email: c.email || '', telefono: c.telefono || '', whatsapp: c.whatsapp || '',
                                        tipo_usuario: c.tipo_usuario || 'Residente', tipo_punto_critico: c.tipo_punto_critico || '',
                                        zona: c.zona || c.distrito_municipal || '', distrito: c.distrito || '', ciudad: c.ciudad || '', pais: c.pais || '',
                                        suscrito_email: !!c.suscrito_email, suscrito_sms: !!c.suscrito_sms, suscrito_whatsapp: !!c.suscrito_whatsapp,
                                        rol: c.rol || 'poblacion', distrito_municipal: c.distrito_municipal || c.zona || '',
                                        tipo_vulnerabilidad: c.tipo_vulnerabilidad || 'ninguna',
                                        medio_comunicacion_preferido: c.medio_comunicacion_preferido || 'whatsapp'
                                    });
                                }}
                                cancelarEdicionContacto={() => setContactoEditando(null)}
                                eliminarContacto={eliminarContacto}
                                darDeBajaContacto={async (id) => {
                                    if (!window.confirm('¿Dar de baja a este contacto?')) return;
                                    try {
                                        const res = await fetch(`${API_BASE}/api/contactos/${id}`, {
                                            method: 'PUT',
                                            headers: getAuthorizedHeaders(),
                                            body: JSON.stringify({ activo: false, motivo_baja: 'solicitada_admin' })
                                        });
                                        if (res.ok) { fetchContactos(); alert('Contacto dado de baja'); }
                                        else alert('Error al dar de baja');
                                    } catch(e) { alert('Error de conexión'); }
                                }}
                                reactivarContacto={async (id) => {
                                    try {
                                        const res = await fetch(`${API_BASE}/api/contactos/${id}`, {
                                            method: 'PUT',
                                            headers: getAuthorizedHeaders(),
                                            body: JSON.stringify({ activo: true, motivo_baja: null })
                                        });
                                        if (res.ok) { fetchContactos(); alert('Contacto reactivado'); }
                                        else alert('Error al reactivar');
                                    } catch(e) { alert('Error de conexión'); }
                                }}
                                importarContactosCSV={importarContactosCSV}
                                consultarJobImportacion={consultarJobImportacion}
                                descargarErroresImportacion={descargarErroresImportacion}
                                listas={listas}
                                fetchListas={fetchListas}
                                fetchContactosDeLista={fetchContactosDeLista}
                                darkMode={darkMode}
                            />
                        </MainLayout>
                    </ProtectedRoute>
                } />

                <Route path="/listas" element={
                    <ProtectedRoute usuario={usuario} verificandoSesion={verificandoSesion}>
                        <MainLayout usuario={usuario} setUsuario={setUsuario} {...darkProps}>
                            <ListasManager darkMode={darkMode} />
                        </MainLayout>
                    </ProtectedRoute>
                } />

                <Route path="/plantillas" element={
                    <ProtectedRoute usuario={usuario} verificandoSesion={verificandoSesion}>
                        <MainLayout usuario={usuario} setUsuario={setUsuario} {...darkProps}>
                            <PlantillasManager
                                registrarActividad={registrarActividad}
                                tiposAlerta={tiposAlerta}
                                onGuardarSuccess={fetchPlantillas}
                                darkMode={darkMode}
                                usuario={usuario}
                            />
                        </MainLayout>
                    </ProtectedRoute>
                } />

                <Route path="/crear-alerta" element={
                    <ProtectedRoute usuario={usuario} verificandoSesion={verificandoSesion}>
                        <MainLayout usuario={usuario} setUsuario={setUsuario} {...darkProps}>
                            <CreateAlert
                                plantillas={plantillas}
                                contactos={contactos}
                                listas={listas}
                                fetchContactosDeLista={fetchContactosDeLista}
                                usuario={usuario}
                                tiposAlerta={tiposAlerta}
                                onVolver={() => window.location.href = '/dashboard'}
                                registrarActividad={registrarActividad}
                                aprobacionRequerida={true}
                                setAlertasPendientes={setAlertasPendientes}
                                alertasPendientes={alertasPendientes}
                                setAlertasProgramadas={setAlertasProgramadas}
                                alertasProgramadas={alertasProgramadas}
                                clienteActivo={clienteActivo}
                                clientes={clientes}
                                darkMode={darkMode}
                            />
                        </MainLayout>
                    </ProtectedRoute>
                } />

                <Route path="/aprobaciones" element={
                    <ProtectedRoute usuario={usuario} verificandoSesion={verificandoSesion}>
                        <MainLayout usuario={usuario} setUsuario={setUsuario} {...darkProps}>
                            <ApprovalQueue usuario={usuario} darkMode={darkMode} />
                        </MainLayout>
                    </ProtectedRoute>
                } />

                <Route path="/clientes" element={
                    <ProtectedRoute usuario={usuario} verificandoSesion={verificandoSesion}>
                        {usuario?.rol === 'superadmin' ? (
                            <MainLayout usuario={usuario} setUsuario={setUsuario} {...darkProps}>
                                <ClientesManager
                                    usuario={usuario}
                                    clienteActivo={clienteActivo}
                                    setClienteActivo={setClienteActivo}
                                    darkMode={darkMode}
                                />
                            </MainLayout>
                        ) : (
                            <Navigate to="/dashboard" />
                        )}
                    </ProtectedRoute>
                } />


                <Route path="/usuarios" element={
                    <ProtectedRoute usuario={usuario} verificandoSesion={verificandoSesion}>
                        <MainLayout usuario={usuario} setUsuario={setUsuario} {...darkProps}>
                            <UserManager
                                usuarios={usuarios}
                                nuevoUsuario={nuevoUsuario}
                                setNuevoUsuario={setNuevoUsuario}
                                usuarioEditando={usuarioEditando}

                                guardarUsuario={guardarUsuario}
                                iniciarEdicionUsuario={(usr) => {
                                    setUsuarioEditando(usr);
                                    setNuevoUsuario({ nombre: usr.nombre, email: usr.email, password: '', rol: usr.rol });
                                }}
                                cancelarEdicionUsuario={() => {
                                    setUsuarioEditando(null);
                                    setNuevoUsuario({ nombre: '', email: '', password: '', rol: 'operador' });
                                }}
                                eliminarUsuario={eliminarUsuario}
                                toggleUsuario={toggleUsuario}
                                usuario={usuario}
                                darkMode={darkMode}
                            />
                        </MainLayout>
                    </ProtectedRoute>
                } />

                <Route path="*" element={<Navigate to="/" />} />
            </Routes>
        </Router>
    );
}

export default App;
